import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MessageSquare, Loader2, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';

export default function Auth() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (user) return <Navigate to="/inbox" replace />;

  return (
    <div className="flex min-h-screen">
      {/* Left: Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-sidebar items-center justify-center p-12">
        <div className="max-w-md space-y-6">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
              <MessageSquare className="h-7 w-7 text-primary-foreground" />
            </div>
            <h1 className="text-3xl font-bold text-sidebar-foreground">GuestMessage<span className="text-primary">AI</span></h1>
          </div>
          <p className="text-lg text-sidebar-foreground/70">
            Il sistema operativo della comunicazione per l'hospitality. Centralizza messaggi, voce e CRM in un'unica console intelligente.
          </p>
          <div className="grid grid-cols-2 gap-4 pt-4">
            {['Multi-canale', 'AI Copilot', 'CRM Integrato', 'Zero Collisioni'].map((f) => (
              <div key={f} className="rounded-lg bg-sidebar-accent px-4 py-3 text-sm font-medium text-sidebar-foreground">
                {f}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Auth Form */}
      <div className="flex w-full lg:w-1/2 items-center justify-center p-6">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardHeader className="text-center lg:hidden pb-2">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary">
                <MessageSquare className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold">GuestMessage<span className="text-primary">AI</span></span>
            </div>
          </CardHeader>
          <CardContent>
            <h1 className="font-semibold tracking-tight hidden lg:block text-xl mb-1">Accedi</h1>
            <CardDescription className="hidden lg:block mb-6">Inserisci le tue credenziali per accedere alla console.</CardDescription>
            <LoginForm />
            <div className="mt-6 pt-6 border-t border-border">
              <MagicLinkPaste />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function LoginForm() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await signIn(email, password);
    if (error) {
      toast.error(error.message);
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="login-email">Email</Label>
        <Input id="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@hotel.com" required />
      </div>
      <div className="space-y-2">
        <Label htmlFor="login-password">Password</Label>
        <Input id="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
      </div>
      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Accedi'}
      </Button>
    </form>
  );
}

function MagicLinkPaste() {
  const [link, setLink] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePaste = async () => {
    const trimmed = link.trim();
    // Extract token from URL like /auto-login?token=xxx
    let token: string | null = null;
    try {
      const url = new URL(trimmed);
      token = url.searchParams.get('token');
    } catch {
      // Maybe they pasted just the token
      if (/^[0-9a-f-]{36}$/i.test(trimmed)) token = trimmed;
    }

    if (!token) {
      toast.error('Link non valido. Incolla il link completo ricevuto.');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('auto-login', {
        body: { token, redirect_to: `${window.location.origin}/inbox` },
      });

      if (error || data?.error) {
        toast.error(data?.error || error?.message || 'Link non valido o scaduto');
        setLoading(false);
        return;
      }

      if (data?.action_link) {
        localStorage.setItem('persistent_login_token', token);
        window.location.replace(data.action_link);
        return;
      }

      toast.error('Risposta non valida');
    } catch (e: any) {
      toast.error(e.message || 'Errore');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <LinkIcon className="h-4 w-4" />
        <span>Accedi con il tuo link di accesso</span>
      </div>
      <div className="flex gap-2">
        <Input
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="Incolla qui il link ricevuto"
          className="text-sm"
        />
        <Button onClick={handlePaste} disabled={loading || !link.trim()} variant="secondary" size="sm" className="shrink-0">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Accedi'}
        </Button>
      </div>
    </div>
  );
}
